// Minimal IndexedDB wrapper for "Coffre" — RGPD safe (browser-only storage)
const DB_NAME = "aia_coffre";
const STORE = "documents";
const META_KEY = "aia_coffre_created";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function isVaultCreated() {
  return localStorage.getItem(META_KEY) === "1";
}

export async function createVault() {
  await openDB(); // ensures DB + store exist
  localStorage.setItem(META_KEY, "1");
  return true;
}

export async function destroyVault() {
  const db = await openDB();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  localStorage.removeItem(META_KEY);
}

export async function addDocument(file) {
  const db = await openDB();
  const id = "doc_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
  const entry = {
    id,
    name: file.name,
    size: file.size,
    type: file.type || "application/octet-stream",
    blob: file,
    createdAt: new Date().toISOString(),
  };
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).add(entry);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  return entry;
}

export async function listDocuments() {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const docs = (req.result || []).map(({ blob, ...meta }) => meta);
      docs.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      res(docs);
    };
    req.onerror = () => rej(req.error);
  });
}

export async function getDocumentBlob(id) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => res(req.result ? req.result.blob : null);
    req.onerror = () => rej(req.error);
  });
}

export async function deleteDocument(id) {
  const db = await openDB();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

export async function renameDocument(id, newName) {
  const trimmed = (newName || "").trim();
  if (!trimmed) return;
  const db = await openDB();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const entry = getReq.result;
      if (!entry) {
        res();
        return;
      }
      entry.name = trimmed;
      const putReq = store.put(entry);
      putReq.onsuccess = () => res();
      putReq.onerror = () => rej(putReq.error);
    };
    getReq.onerror = () => rej(getReq.error);
  });
}

export function formatBytes(bytes) {
  if (!bytes) return "0 o";
  const units = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
